import { differenceInDays, isValid, parseISO } from "date-fns";
import type {
  OnboardingEducation,
  OnboardingExperience,
} from "../../context/OnboardingContext";

/** Job-like object with optional start_date (string or Date). Works with PreviousJobV2 and OnboardingExperience. */
export type PreviousJobLike = { start_date?: string | Date | null };

/**
 * Returns total years of experience from the earliest job's start_date to today.
 * Uses the job with the lowest start_date; fractional years are rounded to the nearest integer.
 *
 * @param jobs - Array of job-like objects (e.g. PreviousJobV2[] or OnboardingExperience[])
 * @returns Years from earliest job start_date to today, or 0 if no valid job
 */
export function getTotalYearsExperienceFromFirstJob(
  jobs: PreviousJobLike[] | null | undefined,
): number {
  if (!jobs?.length) return 0;

  const withValidStart = jobs
    .map((job) => {
      const raw = job?.start_date;
      if (raw == null) return null;
      const startDate = typeof raw === "string" ? parseISO(raw) : raw;
      return isValid(startDate) ? { startDate } : null;
    })
    .filter((x): x is { startDate: Date } => x !== null);

  if (withValidStart.length === 0) return 0;

  const earliest = withValidStart.reduce((min, curr) =>
    curr.startDate.getTime() < min.startDate.getTime() ? curr : min,
  );
  const startDate = earliest.startDate;

  const now = new Date();
  if (startDate.getTime() > now.getTime()) return 0;

  const days = differenceInDays(now, startDate);
  const years = days / 365.25;
  return Math.max(0, Math.round(years));
}

export const sortExperienceByStartDate = (experience: OnboardingExperience[]) =>
  experience.sort((a, b) => {
    const dateA =
      typeof a.start_date === "string" ? new Date(a.start_date) : a.start_date;
    const dateB =
      typeof b.start_date === "string" ? new Date(b.start_date) : b.start_date;
    if (!dateA || !dateB) return 0;
    return dateB.getTime() - dateA.getTime();
  });

export const sortEducationByStartDate = (education: OnboardingEducation[]) =>
  education.sort((a, b) => {
    const dateA =
      typeof a.start_date === "string" ? new Date(a.start_date) : a.start_date;
    const dateB =
      typeof b.start_date === "string" ? new Date(b.start_date) : b.start_date;
    if (!dateA || !dateB) return 0;
    return dateB.getTime() - dateA.getTime();
  });
